/**
 * usePeriodoMutations — hook che incapsula le 2 mutation principali della
 * vista Periodo, condivise tra `MitSection`/`AzioniSection` (legacy) e
 * `DailyTasksSection` (week unified):
 *
 *  1. `assignDate(azId, currentVal, newVal)`
 *     Sposta un'azione a una data specifica (YYYY-MM-DD) o a una settimana
 *     flottante (YYYY-Www). Toast success + reload, toast error in caso di
 *     fallimento.
 *
 *  2. `reassignTask(kind, id, currentData, newData)`
 *     Drag&drop: sposta MIT o Azione tra giorni/flottante. MIT richiede sempre
 *     un giorno (kind='mit' + newData=null → errore toast). Toast con tasto
 *     "Annulla" per ripristinare la posizione precedente.
 */
import { applyOperations, deleteMit, deleteAzione } from '../../lib/api';
import { weekFromIsoDate } from '../../lib/weekFromIsoDate';
import { toast } from 'sonner';

export function usePeriodoMutations({ label, load }) {
  /**
   * Assegna una data (per MIT: solo YYYY-MM-DD; per Azione: YYYY-MM-DD o YYYY-Www).
   *
   * @param {string} id  id della MIT o dell'azione
   * @param {string} currentVal valore attuale (data ISO o settimana ISO)
   * @param {string} newVal nuovo valore desiderato
   * @param {'mit'|'azione'} kind default 'azione' per retrocompat
   */
  const assignDate = async (id, currentVal, newVal, kind = 'azione') => {
    if (!newVal || newVal === currentVal) return;
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(newVal);
    // MIT: unica opzione è una data specifica; se arrivasse una settimana la ignoriamo.
    if (kind === 'mit') {
      if (!isDate) {
        toast.error('Le MIT richiedono un giorno specifico (YYYY-MM-DD)');
        return;
      }
      try {
        await applyOperations([{ entita: 'mit', azione: 'aggiorna', id, campi: { data: newVal } }]);
        toast.success(`MIT spostata al ${newVal}`);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Spostamento MIT fallito');
      }
      return;
    }
    // Azione: data specifica o flottante di settimana
    const campi = isDate
      ? { data: newVal, settimana: weekFromIsoDate(newVal) }
      : { settimana: newVal, data: null };
    try {
      await applyOperations([{ entita: 'azione', azione: 'aggiorna', id, campi }]);
      toast.success(isDate ? `Azione fissata al ${newVal}` : `Spostata a ${newVal.slice(-3)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spostamento azione fallito');
    }
  };

  /** Drag&drop riassegnazione tra giorni. */
  const reassignTask = async (kind, id, currentData, newData) => {
    if (kind === 'mit' && !newData) {
      toast.error('Le MIT richiedono un giorno specifico');
      return;
    }
    if (currentData === newData) return;
    const entita = kind === 'mit' ? 'mit' : 'azione';
    const campi = _buildReassignCampi(kind, newData, label);
    try {
      await applyOperations([{ entita, azione: 'aggiorna', id, campi }]);
      const labelTo = newData ? `al ${newData}` : 'a flottante (settimana)';
      toast.success(`${kind === 'mit' ? 'MIT' : 'Azione'} spostata ${labelTo}`, {
        duration: 4000,
        action: {
          label: 'Annulla',
          onClick: () => _undoReassign(entita, id, kind, currentData, label, load),
        },
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Spostamento fallito');
    }
  };

  const updateText = (kind, id, newText) => _doUpdateText(kind, id, newText, load);
  const deleteTask = (kind, id) => _doDeleteTask(kind, id, load);

  return { assignDate, reassignTask, updateText, deleteTask };
}

/** Aggiorna il testo di una MIT o Azione via applyOperations. */
async function _doUpdateText(kind, id, newText, load) {
  try {
    await applyOperations([{ entita: kind, azione: 'aggiorna', id, campi: { testo: newText } }]);
    toast.success(`${kind === 'mit' ? 'MIT' : 'Azione'} aggiornata`);
    load();
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Aggiornamento testo fallito');
    throw e; // propaga così il TaskTextEditor cancella l'edit
  }
}

/** Elimina definitivamente MIT o Azione via endpoint DELETE. */
async function _doDeleteTask(kind, id, load) {
  try {
    if (kind === 'mit') await deleteMit(id);
    else await deleteAzione(id);
    toast.success(`${kind === 'mit' ? 'MIT' : 'Azione'} eliminata`);
    load();
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Eliminazione fallita');
  }
}

// ── Helper privati ──────────────────────────────────────────────────────

function _buildReassignCampi(kind, newData, label) {
  if (kind === 'mit') return { data: newData };
  if (newData) return { data: newData, settimana: weekFromIsoDate(newData) };
  return { data: null, settimana: label };
}

async function _undoReassign(entita, id, kind, currentData, label, load) {
  try {
    const undoCampi = _buildReassignCampi(kind, currentData, label);
    await applyOperations([{ entita, azione: 'aggiorna', id, campi: undoCampi }]);
    load();
    toast.success('Ripristinato');
  } catch (err) {
    toast.error(err?.response?.data?.detail || 'Errore annullamento');
  }
}
